// WB-INK-001 — pure helpers: stroke grouping + "is this handwriting?" pre-filters (no network, no DOM).
export const INK_IDLE_MS = 1500;
export const INK_TEXT_SIZES = [18, 24, 32, 44];

const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);

export const bboxOf = (points) => {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  points.forEach(([x, y]) => { if (x < x0) x0 = x; if (y < y0) y0 = y; if (x > x1) x1 = x; if (y > y1) y1 = y; });
  return { x: x0, y: y0, w: Math.max(0, x1 - x0), h: Math.max(0, y1 - y0) };
};

export const strokeBox = (s) => {
  const b = bboxOf(s.points);
  const half = (s.width || 0) / 2;
  return { x: b.x - half, y: b.y - half, w: b.w + s.width, h: b.h + s.width };
};

export const unionBox = (boxes) => boxes.reduce((u, b) => (u
  ? { x: Math.min(u.x, b.x), y: Math.min(u.y, b.y), w: Math.max(u.x + u.w, b.x + b.w) - Math.min(u.x, b.x), h: Math.max(u.y + u.h, b.y + b.h) - Math.min(u.y, b.y) }
  : { ...b }), null);

export const boxesIntersect = (a, b, gap = 0) => a.x - gap <= b.x + b.w && b.x - gap <= a.x + a.w && a.y - gap <= b.y + b.h && b.y - gap <= a.y + a.h;
export const boxContains = (outer, inner, pad = 0) => inner.x >= outer.x - pad && inner.y >= outer.y - pad
  && inner.x + inner.w <= outer.x + outer.w + pad && inner.y + inner.h <= outer.y + outer.h + pad;

const pathLength = (pts) => pts.reduce((acc, p, i) => (i ? acc + dist(pts[i - 1], p) : 0), 0);
const median = (arr) => { const s = [...arr].sort((a, b) => a - b); return s.length ? s[Math.floor(s.length / 2)] : 0; };

export const isNearStraight = (s) => {
  const pts = s.points;
  if (pts.length < 4) return true;
  const len = pathLength(pts);
  return len > 0 && dist(pts[0], pts[pts.length - 1]) / len > 0.93;
};

export const isClosedShape = (s) => {
  const pts = s.points, b = bboxOf(pts);
  if (pts.length < 10 || b.w < 60 || b.h < 60) return false;
  return dist(pts[0], pts[pts.length - 1]) < 0.12 * pathLength(pts);
};

// Same writing line, close enough horizontally to be one word/phrase (anisotropic: words on a line merge, lines don't).
export const nearBoxes = (a, b, gx, gy) => a.x - gx <= b.x + b.w && b.x - gx <= a.x + a.w && a.y - gy <= b.y + b.h && b.y - gy <= a.y + a.h;

// Union-find clustering on expanded bounding boxes (strokes of one word/phrase touch or nearly touch).
export const clusterStrokes = (strokes) => {
  const boxes = strokes.map(strokeBox);
  const h = median(boxes.map((b) => b.h));
  const gx = Math.max(40, 2 * h), gy = Math.max(12, 0.35 * h);
  const parent = strokes.map((_, i) => i);
  const find = (i) => (parent[i] === i ? i : (parent[i] = find(parent[i])));
  for (let i = 0; i < boxes.length; i += 1) {
    for (let j = i + 1; j < boxes.length; j += 1) if (nearBoxes(boxes[i], boxes[j], gx, gy)) parent[find(i)] = find(j);
  }
  const groups = new Map();
  strokes.forEach((s, i) => { const r = find(i); if (!groups.has(r)) groups.set(r, []); groups.get(r).push(s); });
  return [...groups.values()];
};

// Does a fresh stroke continue this group (next letter, i-dot, t-cross, next word on the same line)?
export const strokeContinuesGroup = (box, stroke) => {
  const b = strokeBox(stroke);
  const h = Math.max(box.h, 12);
  return b.x <= box.x + box.w + 2.5 * h && b.x + b.w >= box.x - 1.5 * h
    && b.y + b.h >= box.y - 0.6 * h && b.y <= box.y + box.h + 0.4 * h;
};

export const assessGroup = (strokes) => {
  if (!strokes.length) return { ok: false, reason: 'empty' };
  const box = unionBox(strokes.map(strokeBox));
  if (strokes.length > 60) return { ok: false, reason: 'too_many_strokes', box };
  if (box.h < 10 || box.w < 8) return { ok: false, reason: 'too_small', box };
  if (box.h > 220) return { ok: false, reason: 'too_tall', box };
  if (box.w > 1400) return { ok: false, reason: 'too_wide', box };
  if (strokes.length === 1) {
    const s = strokes[0];
    if (isNearStraight(s)) return { ok: false, reason: 'line', box };
    if (isClosedShape(s)) return { ok: false, reason: 'shape', box };
    if (box.w > 180 && box.h > 120) return { ok: false, reason: 'drawing', box };
  }
  if (strokes.length <= 3 && strokes.every(isNearStraight) && box.w > 3 * box.h) return { ok: false, reason: 'arrow', box };
  if (strokes.length <= 6 && strokes.every(isNearStraight) && box.w > 120 && box.h > 100) return { ok: false, reason: 'shape', box };
  return { ok: true, box };
};

// Every cluster in a sealed burst that looks like handwriting (each becomes its own recognition group).
export const pickCandidates = (burst) => clusterStrokes(burst)
  .map((cluster) => ({ cluster, verdict: assessGroup(cluster) }))
  .filter(({ verdict }) => verdict.ok)
  .map(({ cluster, verdict }) => ({ strokes: cluster, box: verdict.box }));

// Picks the cluster that contains the most recent stroke, if it looks like handwriting.
export const pickCandidate = (burst) => {
  if (!burst.length) return null;
  const last = burst[burst.length - 1];
  return pickCandidates(burst).find((c) => c.strokes.includes(last)) || null;
};

export const suggestTextSize = (boxH, lines = 1, sizes = INK_TEXT_SIZES) => {
  const target = (boxH / Math.max(1, lines)) * 0.8;
  return sizes.reduce((best, s) => (Math.abs(s - target) < Math.abs(best - target) ? s : best), sizes[0]);
};
